import React,{useEffect,useMemo,useState}from"react";
import{Box,Button,FormControl,InputLabel,MenuItem,Paper,Select,Table,TableBody,TableCell,TableContainer,TableHead,TablePagination,TableRow,TextField,Typography,useMediaQuery}from"@mui/material";
import{useNavigate}from"react-router-dom";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAuth}from"../../context/AuthContext";
import{useAlert}from"../../context/AlertContext";

const API_BASE="/api/purchases";

function getToken(){
  return localStorage.getItem("accessToken")||localStorage.getItem("access")||"";
}

function userCan(user,code){
  if(!user)return false;
  if(user.role==="super_admin"||user.role==="admin")return true;
  return(user.permissions||[]).includes(code);
}

function formatDate(value){
  if(!value)return"-";
  const parts=String(value).split("-");
  if(parts.length===3)return`${parts[2]}/${parts[1]}/${parts[0]}`;
  return value;
}

function formatMoney(value){
  return Number(value||0).toFixed(2);
}

function documentLabel(value){
  const labels={FACTURA:"Factura",NOTA_VENTA:"Nota de venta",RECIBO:"Recibo",SIN_COMPROBANTE:"Sin comprobante",OTRO:"Otro"};
  return labels[value]||value||"-";
}

export default function AdminPurchases(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{user}=useAuth();
  const{showError}=useAlert();
  const navigate=useNavigate();
  const isMobile=useMediaQuery("(max-width:900px)");
  const token=getToken();
  const[empresaId,setEmpresaId]=useState("");
  const[empresaLoading,setEmpresaLoading]=useState(true);
  const[items,setItems]=useState([]);
  const[loading,setLoading]=useState(false);
  const[search,setSearch]=useState("");
  const[estado,setEstado]=useState("todos");
  const[tipoDocumento,setTipoDocumento]=useState("todos");
  const[fechaDesde,setFechaDesde]=useState("");
  const[fechaHasta,setFechaHasta]=useState("");
  const[page,setPage]=useState(0);
  const rowsPerPage=isMobile?10:30;
  const canView=userCan(user,"compras.view");
  const canCreate=userCan(user,"compras.create");
  const showActions=canView;
  const tableColumns=showActions?8:7;

  const visibleItems=useMemo(()=>{
    const start=page*rowsPerPage;
    return items.slice(start,start+rowsPerPage);
  },[items,page,rowsPerPage]);

  const loadEmpresa=async()=>{
    try{
      setEmpresaLoading(true);
      const response=await fetch("/api/enterprise/empresa/obtener/",{headers:{Authorization:`Bearer ${token}`}});
      const data=await response.json();

      if(!response.ok){
        showError(data.error||data.detail||"No se pudo cargar la empresa.");
        return;
      }

      if(!data?.empresa?.id){
        showError("No existe una empresa configurada.");
        return;
      }

      setEmpresaId(data.empresa.id);
    }catch{
      showError("Error de conexión al cargar la empresa.");
    }finally{
      setEmpresaLoading(false);
    }
  };

  const loadPurchases=async()=>{
    if(!empresaId||!canView)return;

    try{
      setLoading(true);

      const params=new URLSearchParams({empresa_id:empresaId});

      if(search.trim())params.append("q",search.trim());
      if(estado!=="todos")params.append("estado",estado);
      if(tipoDocumento!=="todos")params.append("tipo_documento",tipoDocumento);
      if(fechaDesde)params.append("fecha_desde",fechaDesde);
      if(fechaHasta)params.append("fecha_hasta",fechaHasta);

      const response=await fetch(`${API_BASE}/?${params.toString()}`,{
        headers:{Authorization:`Bearer ${token}`}
      });

      const data=await response.json();

      if(!response.ok){
        showError(typeof data.error==="string"?data.error:data.detail||"No se pudieron cargar las compras y gastos.");
        setItems([]);
        return;
      }

      setItems(Array.isArray(data.results)?data.results:[]);
    }catch{
      setItems([]);
      showError("Error de conexión.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadEmpresa();
  },[]);

  useEffect(()=>{
    if(!empresaId)return;

    const delay=setTimeout(()=>{
      setPage(0);
      loadPurchases();
    },350);

    return()=>clearTimeout(delay);
  },[empresaId,search,estado,tipoDocumento,fechaDesde,fechaHasta,canView]);

  useEffect(()=>{
    setPage(0);
  },[rowsPerPage]);

  const outlinedButtonSx={
    minHeight:40,
    px:2.4,
    borderRadius:"12px",
    textTransform:"none",
    fontWeight:700,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none"
    }
  };

  const smallButtonSx={
    minHeight:32,
    px:1.5,
    borderRadius:"8px",
    textTransform:"none",
    fontWeight:600,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none"
    }
  };

  const selectSx={
    minWidth:{xs:"100%",sm:155},
    ...dashboardTheme.inputStyles,
    "& .MuiSelect-icon":{
      color:dashboardTheme.text_color
    }
  };

  const selectMenuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        mt:.5,
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none",
        "& .MuiMenu-list":{
          p:.5
        },
        "& .MuiMenuItem-root":{
          fontSize:14,
          borderRadius:"7px",
          color:dashboardTheme.text_color,
          "&:hover":{
            bgcolor:dashboardTheme.dashboard_background
          },
          "&.Mui-selected":{
            bgcolor:dashboardTheme.dashboard_background
          }
        }
      }
    }
  };

  const secondaryText=dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.62)":"#64748b";

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>

      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>

        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"95%"},mx:"auto",mt:{xs:1,md:1.5}}}>

            <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,mb:{xs:1.5,md:2}}}>
              <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>
                Compras y Gastos
              </Typography>

              <Box sx={{display:"flex",flexDirection:{xs:"column",sm:"row"},gap:1}}>
                {canView&&(
                  <Button variant="outlined" onClick={()=>navigate("/admin-panel/compras/categorias")} sx={outlinedButtonSx}>
                    Categorías
                  </Button>
                )}

                {canCreate&&(
                  <Button variant="contained" onClick={()=>navigate("/admin-panel/compras/nueva")} sx={dashboardTheme.buttonStyles.contained}>
                    Nueva compra o gasto
                  </Button>
                )}
              </Box>
            </Box>

            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box sx={{...dashboardTheme.tableStyles.toolbar,display:"flex",flexWrap:"wrap",alignItems:"center",gap:1}}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar persona o documento"
                  value={search}
                  onChange={e=>setSearch(e.target.value)}
                  sx={{maxWidth:{xs:"100%",md:390},...dashboardTheme.inputStyles}}
                />

                <FormControl size="small" sx={selectSx}>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    label="Estado"
                    value={estado}
                    onChange={e=>setEstado(e.target.value)}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="REGISTRADO">Registrado</MenuItem>
                    <MenuItem value="ANULADO">Anulado</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={selectSx}>
                  <InputLabel>Documento</InputLabel>
                  <Select
                    label="Documento"
                    value={tipoDocumento}
                    onChange={e=>setTipoDocumento(e.target.value)}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="FACTURA">Factura</MenuItem>
                    <MenuItem value="NOTA_VENTA">Nota de venta</MenuItem>
                    <MenuItem value="RECIBO">Recibo</MenuItem>
                    <MenuItem value="SIN_COMPROBANTE">Sin comprobante</MenuItem>
                    <MenuItem value="OTRO">Otro</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  type="date"
                  label="Desde"
                  value={fechaDesde}
                  onChange={e=>setFechaDesde(e.target.value)}
                  InputLabelProps={{shrink:true}}
                  sx={{minWidth:{xs:"100%",sm:155},...dashboardTheme.inputStyles}}
                />

                <TextField
                  size="small"
                  type="date"
                  label="Hasta"
                  value={fechaHasta}
                  onChange={e=>setFechaHasta(e.target.value)}
                  InputLabelProps={{shrink:true}}
                  sx={{minWidth:{xs:"100%",sm:155},...dashboardTheme.inputStyles}}
                />
              </Box>

              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={{...dashboardTheme.tableStyles.table,minWidth:980}}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:120}}>Emisión</TableCell>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:230}}>Persona</TableCell>
                        <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:190}}>Documento</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Neto</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Imp.</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Total</TableCell>
                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Saldo</TableCell>
                        {showActions&&(
                          <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                            Acciones
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {visibleItems.map(item=>(
                        <TableRow
                          key={item.id}
                          hover
                          sx={{...dashboardTheme.tableStyles.bodyRow,cursor:"pointer"}}
                          onDoubleClick={()=>navigate(`/admin-panel/compras/${item.id}`)}
                        >
                          <TableCell sx={{whiteSpace:"nowrap",fontWeight:600}}>
                            {formatDate(item.fecha_documento)}
                          </TableCell>

                          <TableCell>
                            <Typography sx={{fontSize:14,fontWeight:700,color:dashboardTheme.text_color,lineHeight:1.25}}>
                              {item.tercero_razon_social||item.tercero?.razon_social||"-"}
                            </Typography>

                            <Typography sx={{fontSize:11.5,fontWeight:500,color:secondaryText,mt:.25}}>
                              {item.tercero_identificacion||"Proveedor"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography sx={{fontSize:13.5,fontWeight:700,color:dashboardTheme.primary_color}}>
                              {item.numero_documento||"-"}
                            </Typography>

                            <Typography sx={{fontSize:11.5,fontWeight:500,color:secondaryText,mt:.25}}>
                              {documentLabel(item.tipo_documento)}
                            </Typography>
                          </TableCell>

                          <TableCell align="right" sx={{whiteSpace:"nowrap",fontWeight:600}}>
                            ${formatMoney(item.subtotal)}
                          </TableCell>

                          <TableCell align="right" sx={{whiteSpace:"nowrap",fontWeight:600}}>
                            ${formatMoney(item.iva)}
                          </TableCell>

                          <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                            <Typography sx={{fontSize:14,fontWeight:700,color:dashboardTheme.text_color}}>
                              ${formatMoney(item.total)}
                            </Typography>
                          </TableCell>

                          <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                            <Typography sx={{
                              fontSize:14,
                              fontWeight:700,
                              color:Number(item.saldo_pendiente)>0?"#D97706":dashboardTheme.text_color
                            }}>
                              ${formatMoney(item.saldo_pendiente)}
                            </Typography>
                          </TableCell>

                          {showActions&&(
                            <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={e=>{
                                  e.stopPropagation();
                                  navigate(`/admin-panel/compras/${item.id}`);
                                }}
                                sx={smallButtonSx}
                              >
                                Ver
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}

                      {!loading&&!empresaLoading&&visibleItems.length===0&&canView&&(
                        <TableRow>
                          <TableCell colSpan={tableColumns} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                            No hay compras o gastos que coincidan con los filtros
                          </TableCell>
                        </TableRow>
                      )}

                      {(loading||empresaLoading)&&(
                        <TableRow>
                          <TableCell colSpan={tableColumns} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                            Cargando compras y gastos...
                          </TableCell>
                        </TableRow>
                      )}

                      {!canView&&(
                        <TableRow>
                          <TableCell colSpan={tableColumns} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                            No tienes permisos para ver compras y gastos
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <TablePagination
                component="div"
                count={items.length}
                page={page}
                onPageChange={(_event,newPage)=>setPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`}
                sx={dashboardTheme.tableStyles.pagination}
              />
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}